import { useState, useEffect } from "react";
import { getFreeExpressDeliveryPromoBanner } from "../../services/order";
import Ripple from "../ui/spinners/ripple/Ripple";
import classes from "./PromoBanner.module.css";

const PromoBanner = () => {
  const [promo, setPromo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);

  // Fetch promo data
  const fetchPromoData = async () => {
    try {
      setIsLoading(true);
      const response = await getFreeExpressDeliveryPromoBanner();
      const data = response?.data;

      if (data?.isActiveNow) {
        setPromo(data);
        setIsExpired(false);
      } else {
        setPromo(null);
        setIsExpired(true);
      }
    } catch (error) {
      console.error("Failed to fetch promo banner:", error);
      setPromo(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch promo on component mount and set up refetch interval (30 minutes)
  useEffect(() => {
    fetchPromoData();

    // Refetch every 30 minutes
    const refetchInterval = setInterval(
      () => {
        fetchPromoData();
      },
      30 * 60 * 1000
    ); // 30 minutes

    return () => clearInterval(refetchInterval);
  }, []);

  // Update countdown timer
  useEffect(() => {
    if (!promo?.isActiveNow) return;

    const updateTimer = () => {
      const timeLeftInSeconds = promo?.timeLeftToEndInSeconds || 0;

      if (timeLeftInSeconds <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(timeLeftInSeconds / (24 * 3600));
      const hours = Math.floor((timeLeftInSeconds % (24 * 3600)) / 3600);
      const minutes = Math.floor((timeLeftInSeconds % 3600) / 60);
      const seconds = timeLeftInSeconds % 60;

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateTimer();

    // Update every second
    const timerInterval = setInterval(() => {
      setPromo((prev) => {
        if (!prev) return prev;

        const newTimeLeft = Math.max(0, prev.timeLeftToEndInSeconds - 1);

        if (newTimeLeft === 0) {
          setIsExpired(true);
        }

        return { ...prev, timeLeftToEndInSeconds: newTimeLeft };
      });

      updateTimer();
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [promo]);

  if (isLoading) {
    return (
      <div className={classes["promo-banner__loading"]}>
        <Ripple />
      </div>
    );
  }

  if (!promo || isExpired) {
    return null;
  }

  return (
    <div className={classes["promo-banner"]}>
      <div className={classes["promo-banner__content"]}>
        <div className={classes["promo-banner__close"]}>
          <button
            className={classes["close-btn"]}
            onClick={() => setPromo(null)}
            aria-label="Close banner"
          >
            ✕
          </button>
        </div>
        <div className={classes["promo-banner__text"]}>
          <h2 className={classes["promo-banner__title"]}>
            شحن سريع مجاني ! 🚚
          </h2>
          <p className={classes["promo-banner__subtitle"]}>
            استمتع بشحن سريع مجاني لطلبك! العرض لفترة محدودة.
          </p>
        </div>

        <div className={classes["promo-banner__timer"]}>
          <div className={classes["timer-box"]}>
            <div className={classes["timer-value"]}>
              {String(timeLeft.hours).padStart(2, "0")}
            </div>
            <div className={classes["timer-label"]}>ساعة</div>
          </div>

          <div className={classes["timer-separator"]}>:</div>

          <div className={classes["timer-box"]}>
            <div className={classes["timer-value"]}>
              {String(timeLeft.minutes).padStart(2, "0")}
            </div>
            <div className={classes["timer-label"]}>دقيقة</div>
          </div>

          <div className={classes["timer-separator"]}>:</div>

          <div className={classes["timer-box"]}>
            <div className={classes["timer-value"]}>
              {String(timeLeft.seconds).padStart(2, "0")}
            </div>
            <div className={classes["timer-label"]}>ثانية</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
